/**
 * Modello della superficie **Avvio processo SLA** (piano Client sez. 5 F2b e
 * sez. 8 riga 124).
 *
 * Questo file e' l'unico posto in cui la bozza di avvio viene costruita,
 * confrontata coi default letti dal modello e convertita nel frammento di
 * payload che l'host (`StartProcess.tsx`) unisce al **POST di avvio esistente**.
 * I componenti non toccano i DTO e non fanno calcoli di calendario: secondi,
 * scadenze e rischio arrivano dal Server (`previewSlaDefinition`).
 *
 * Regole codificate (sez. 5 F2b):
 * - **default leggibile anche non-admin**: i default sono le definizioni lette
 *   con `fetchSlaDefinitions`, senza alcun filtro di ruolo lato client. Il ruolo
 *   governa **solo** l'override, non la lettura;
 * - **override solo admin**: `SLA_START_ADMIN_ONLY_*` sono le uniche proprieta'
 *   che l'utente non admin non puo' modificare;
 * - **deadline assoluta solo processo**: `slaStartAbsoluteDeadlineAllowed`
 *   ammette `absolute_deadline_at` sul solo target di processo; sul target
 *   attivita' il campo non e' rappresentabile, coerente con la colonna
 *   (`sla_policy_target.absolute_deadline_at`, `Models/SlaDefinition.pas:173`)
 *   e con l'assenza di deadline assoluta in `sla_definition` a design-time
 *   (sez. 5 F2a);
 * - **timezone esplicita**: la deadline assoluta e' un orario di parete
 *   nell'organizzazione, convertito in istante UTC con `dayjs.tz(..., tz)`;
 *   nessun uso della timezone del browser;
 * - **task futuri conservati**: la bozza di ogni target sopravvive alle risposte
 *   della preview; nessuna risposta di preview riscrive una bozza.
 */

import {
  SLA_FORMAT_ABSOLUTE,
  SLA_FORMAT_RELATIVE,
  SLA_TARGET_PROCESS,
} from 'model/slaModel';
import type {
  SlaDefinitionDto,
  SlaDurationFormat,
  SlaDurationUnit,
  SlaPreviewRequest,
  SlaTargetType,
} from 'model/slaModel';

// ─── Chiavi di traduzione ────────────────────────────────────────────────────

/**
 * Chiavi usate dalla superficie di avvio. Raccolte qui e non sparse nei
 * componenti perche' il piano sez. 4 le fa concordare con Python P1 in un
 * manifest: `getTranslation` restituisce la chiave se il testo manca, quindi
 * questo elenco **e'** il manifest lato avvio. Nessun testo e' scritto nel
 * codice e nessuna label usa un enum italiano.
 */
export const SLA_START_TEXT_KEYS = {
  title: 'sla.start.title',
  /** I default non modificati: lettura, non configurazione. */
  defaultsTitle: 'sla.start.defaults.title',
  /** Target senza SLA configurato nel modello: assenza neutra, non un verde. */
  targetNoSla: 'sla.start.target.no_sla',
  overrideTitle: 'sla.start.override.title',
  /** Solo admin: la sezione non compare per gli altri ruoli (sez. 5 F2b). */
  overrideHint: 'sla.start.override.hint',
  overrideDuration: 'sla.start.override.duration',
  /** Selettore del tipo di scadenza del processo (solo admin, solo processo). */
  overrideMode: 'sla.start.override.mode',
  overrideModeRelative: 'sla.start.override.mode.relative',
  overrideModeAbsolute: 'sla.start.override.mode.absolute',
  overrideDeadline: 'sla.start.override.deadline',
  /** La deadline assoluta e' ammessa sul solo processo; sull'attivita' si spiega perche'. */
  overrideDeadlineProcessOnly: 'sla.start.override.deadline.process_only',
  overrideReason: 'sla.start.override.reason',
  overrideReset: 'sla.start.override.reset',
  /** Timezone dell'organizzazione mostrata accanto all'orario di parete. */
  timezone: 'sla.start.timezone',
  timezoneUnavailable: 'sla.start.timezone.unavailable',
  /** Il 403 della lettura default (rotta admin-only lato Server) va detto in chiaro. */
  forbidden: 'sla.start.forbidden',
  loadError: 'sla.start.load_error',
  loading: 'sla.start.loading',
  previewTitle: 'sla.start.preview.title',
  previewDeadline: 'sla.start.preview.deadline',
  previewRisk: 'sla.start.preview.risk',
  previewAsOf: 'sla.start.preview.as_of',
  previewRevision: 'sla.start.preview.revision',
  /** `estimable = false`: stima non disponibile e' un avviso, non un errore (F2c). */
  previewUnavailable: 'sla.start.preview.unavailable',
  previewRefresh: 'sla.start.preview.refresh',
  /** Validazione locale dell'override: namespace `sla.validation.*`, come l'editor. */
  reasonRequired: 'sla.validation.reason_required',
  deadlineInPast: 'sla.validation.deadline_in_past',
  /** Riga riassuntiva (sezione chiusa): SLA del processo preso dal modello. */
  summaryDefault: 'sla.start.summary.default',
  /** Riga riassuntiva: SLA del processo modificato per questo avvio. */
  summaryCustom: 'sla.start.summary.custom',
  /** Riga riassuntiva per chi non e' admin: default applicato, non modificabile. */
  summaryLocked: 'sla.start.summary.locked',
  /** Comando che apre la modale (solo admin). */
  summaryEdit: 'sla.start.summary.edit',
  /** Titolo della modale SLA dell'avvio. */
  modalTitle: 'sla.start.modal.title',
  /** Riga riassuntiva in errore: la configurazione SLA va corretta prima dell'avvio. */
  summaryInvalid: 'sla.start.summary.invalid',
  /** Azioni della modale: stesse chiavi della modale "Dati utente" (`FieldsList.tsx`). */
  modalCancel: 'pm.startprocess.button.cancel.caption',
  modalConfirm: 'pm.mayactivities.confirmation.label',
} as const;

// ─── Contratto del payload di avvio ──────────────────────────────────────────

/**
 * Chiave del frammento SLA nel POST di avvio. Il piano Server sez. 4 S2 punto 1
 * prescrive "payload sla integrato nella richiesta esistente" e lo schema
 * dichiara `sla_policy_target.origin = 2 = override iniziale all'avvio`
 * (`Models/SlaDefinition.pas:154`), ma `TProcessStart` non ha oggi alcun campo
 * SLA e `EnsureProcessPolicyForStart` copia solo voci con `origin = default`
 * (`Service.SLA.Transitions.pas:325`): la chiave e la forma qui sotto sono la
 * proposta Client, isolata in una costante perche' vada allineata in un punto
 * solo quando il Server la implementa. Un campo JSON non riconosciuto viene
 * ignorato dal controller DMVC, quindi l'invio non rompe l'avvio esistente.
 */
export const SLA_START_PAYLOAD_KEY = 'sla';

/** Chiave con cui il frammento dichiara la revisione su cui e' stato calcolato. */
export const SLA_START_PAYLOAD_REVISION_FIELD = 'model_revision';

// ─── Bozza per target ────────────────────────────────────────────────────────

/**
 * Bozza di avvio di un target. Estende la vista della definizione con cio' che
 * esiste **solo** nello snapshot di avvio: `absoluteDeadlineAt` (la policy ha
 * `absolute_deadline_at`, la definizione no) e `overridden`, che distingue il
 * default letto dal modello dalla modifica dell'admin.
 *
 * Il campo `sourceDurationFormat` conserva il formato **letto** dal Server: un
 * default assoluto storico non viene riscritto in silenzio.
 */
export interface SlaStartDraft {
  targetType: SlaTargetType;
  /** null = target di processo (`target_designerid` e' NULL nel contratto). */
  targetDesignerId: string | null;
  /** Nome leggibile: dal modello per il processo, dal designerid per i nodi. */
  name: string;
  durationValue: number | null;
  durationUnit: SlaDurationUnit | null;
  riskRemainingPercent: number | null;
  responsibleUserId: number | null;
  responsibleRoleId: number | null;
  /**
   * Istante ISO 8601 **UTC** della deadline assoluta: e' l'unico campo che la UI
   * compone a partire dall'orario di parete nella timezone dell'organizzazione.
   * Ammesso dal solo target di processo (`slaStartAbsoluteDeadlineAllowed`).
   */
  absoluteDeadlineAt: string | null;
  /** Il target non ha definizione nel modello: non e' un default modificato. */
  hasModelSla: boolean;
  /** Duration format letto dal Server, per non riscrivere un assoluto esistente. */
  sourceDurationFormat: SlaDurationFormat;
}

/**
 * Nomi leggibili dei nodi dal BPMN XML del modello (`id` -> `name`): le
 * definizioni SLA portano solo `target_designerid`. Un nodo senza nome resta
 * assente dalla mappa e la UI ricade sull'identificativo. XML non leggibile =
 * mappa vuota, nessuna eccezione.
 */
export const slaNodeNamesFromBpmnXml = (xml: string): Record<string, string> => {
  const names: Record<string, string> = {};
  try {
    const doc = new DOMParser().parseFromString(xml, 'application/xml');
    if (doc.getElementsByTagName('parsererror').length > 0) return names;
    const all = doc.getElementsByTagName('*');
    for (let index = 0; index < all.length; index += 1) {
      const element = all[index];
      const id = element.getAttribute('id');
      const name = element.getAttribute('name')?.trim();
      if (id && name && !(id in names)) names[id] = name;
    }
  } catch {
    // XML non valido: nessun nome.
  }
  return names;
};

/** Chiave stabile di un target nella bozza e negli identificatori DOM. */
export const slaStartTargetKey = (
  draft: Pick<SlaStartDraft, 'targetType' | 'targetDesignerId'>,
): string => `${draft.targetType}:${draft.targetDesignerId ?? ''}`;

/**
 * Bozza vuota per un target del modello senza definizione SLA. `hasModelSla` e'
 * false: il target compare nell'elenco (l'assenza e' un dato, sez. 11.2) ma non
 * produce alcun invio finche' non viene modificato.
 */
export const emptySlaStartDraft = (draft: {
  targetType: SlaTargetType;
  targetDesignerId: string | null;
  name: string;
}): SlaStartDraft => ({
  ...draft,
  durationValue: null,
  durationUnit: null,
  riskRemainingPercent: null,
  responsibleUserId: null,
  responsibleRoleId: null,
  absoluteDeadlineAt: null,
  hasModelSla: false,
  sourceDurationFormat: SLA_FORMAT_RELATIVE,
});

/** Default di avvio letti dal modello: uno per definizione restituita. */
export const slaStartDraftFromDefinition = (
  dto: SlaDefinitionDto,
  name: string,
): SlaStartDraft => ({
  targetType: dto.targettype,
  targetDesignerId: dto.targetdesignerid,
  name,
  durationValue: dto.durationvalue,
  durationUnit: dto.durationunit,
  riskRemainingPercent: dto.riskremainingpercent,
  responsibleUserId: dto.responsibleuserid,
  responsibleRoleId: dto.responsibleroleid,
  absoluteDeadlineAt: null,
  hasModelSla: true,
  sourceDurationFormat: dto.durationformat,
});

/**
 * Deadline assoluta: **solo** il target di processo (sez. 5 F2b). E' la stessa
 * regola della colonna `sla_policy_target.absolute_deadline_at`, che esiste
 * nello snapshot di avvio ma non in `sla_definition` a design-time.
 */
export const slaStartAbsoluteDeadlineAllowed = (
  targetType: SlaTargetType,
): boolean => targetType === SLA_TARGET_PROCESS;

/** true quando il target ha i dati minimi per produrre uno SLA (durata > 0). */
export const isSlaStartDraftConfigured = (draft: SlaStartDraft): boolean =>
  draft.durationValue !== null && draft.durationValue > 0;

// ─── Override (admin-only) ───────────────────────────────────────────────────

/**
 * Campi dell'override iniziale: sono esattamente le proprieta' che il piano
 * Server S2 punto 1 riserva all'autorizzazione admin-only. Non includono il
 * formato/valore di durata letto? Si': l'override e' l'intera voce di policy,
 * quindi anche `duration_*` e' un campo di override.
 */
export const SLA_START_ADMIN_ONLY_FIELDS = [
  'durationValue',
  'durationUnit',
  'absoluteDeadlineAt',
] as const;

export type SlaStartAdminOnlyField = (typeof SLA_START_ADMIN_ONLY_FIELDS)[number];

/**
 * true quando la bozza diverge dal default letto dal modello. Un target senza
 * definizione (`hasModelSla = false`) non e' un override: non c'e' nulla da
 * sovrascrivere e non viene inviato.
 */
export const slaStartDraftIsOverridden = (
  draft: SlaStartDraft,
  defaults: SlaStartDraft[],
): boolean => {
  const base = defaults.find(
    (item) => slaStartTargetKey(item) === slaStartTargetKey(draft),
  );
  if (!base) return false;
  return (
    base.durationValue !== draft.durationValue ||
    base.durationUnit !== draft.durationUnit ||
    base.riskRemainingPercent !== draft.riskRemainingPercent ||
    base.responsibleUserId !== draft.responsibleUserId ||
    base.responsibleRoleId !== draft.responsibleRoleId ||
    draft.absoluteDeadlineAt !== null
  );
};

export interface SlaStartDraftValidation {
  /** Bloccano l'invio: guardie di forma della UI, non la validazione del Server. */
  errors: string[];
}

/**
 * Validazione locale dell'override. `reason` e' obbligatorio **solo se c'e' un
 * override** (stessa regola del PATCH `/instances/{id}`, 422
 * `sla_reason_required`) e una deadline assoluta gia' passata e' rifiutata dalla
 * UI **prima** dell'invio: il Server risponde `sla_deadline_in_past` e non
 * avvia.
 */
export const validateSlaStartOverride = (
  defaults: SlaStartDraft[],
  drafts: SlaStartDraft[],
  reason: string,
  referenceAt: string,
): SlaStartDraftValidation => {
  const errors: string[] = [];
  const overridden = drafts.filter((draft) =>
    slaStartDraftIsOverridden(draft, defaults),
  );
  if (overridden.length === 0) return { errors };

  if (reason.trim() === '') errors.push(SLA_START_TEXT_KEYS.reasonRequired);
  if (
    overridden.some(
      (draft) =>
        draft.absoluteDeadlineAt !== null &&
        new Date(draft.absoluteDeadlineAt).getTime() <=
          new Date(referenceAt).getTime(),
    )
  ) {
    errors.push(SLA_START_TEXT_KEYS.deadlineInPast);
  }
  return { errors };
};

// ─── Frammento di payload per il POST di avvio ───────────────────────────────

/** Voce del frammento: chiavi snake_case, come i payload del contratto SLA. */
export interface SlaStartPayloadTarget {
  target_type: SlaTargetType;
  target_designerid?: string | null;
  duration_format: SlaDurationFormat;
  duration_value?: number | null;
  duration_unit?: SlaDurationUnit | null;
  risk_remaining_percent?: number | null;
  responsible_user_id?: number | null;
  responsible_role_id?: number | null;
  /** ISO 8601 UTC; presente solo sul target di processo. */
  absolute_deadline_at?: string | null;
}

export interface SlaStartPayload {
  model_revision: number;
  reason?: string;
  targets: SlaStartPayloadTarget[];
}

/**
 * Frammento da unire al payload del **POST di avvio esistente**: una sola
 * richiesta, nessuna seconda chiamata di override (sez. 5 F2b "override
 * atomico"). Vengono inviati i soli target con un default o un override; un
 * target senza SLA e senza modifica non entra.
 */
export const buildSlaStartPayload = (
  defaults: SlaStartDraft[],
  drafts: SlaStartDraft[],
  modelRevision: number,
  reason: string,
): SlaStartPayload => ({
  [SLA_START_PAYLOAD_REVISION_FIELD]: modelRevision,
  ...(reason.trim() !== '' ? { reason: reason.trim() } : {}),
  targets: drafts
    .filter(
      (draft) =>
        // Un processo senza SLA nel modello entra solo se l'admin gli ha dato
        // una durata (override) o una deadline assoluta.
        (isSlaStartDraftConfigured(draft) &&
          (draft.hasModelSla || slaStartDraftIsOverridden(draft, defaults))) ||
        draft.absoluteDeadlineAt !== null,
    )
    .map((draft) => ({
      target_type: draft.targetType,
      target_designerid: draft.targetDesignerId,
      duration_format: draft.sourceDurationFormat,
      duration_value: draft.durationValue,
      duration_unit: draft.durationUnit,
      risk_remaining_percent: draft.riskRemainingPercent,
      responsible_user_id: draft.responsibleUserId,
      responsible_role_id: draft.responsibleRoleId,
      // La deadline assoluta esiste solo sul processo: sugli altri target il
      // campo non viene nemmeno emesso, non emesso nullo.
      ...(slaStartAbsoluteDeadlineAllowed(draft.targetType)
        ? { absolute_deadline_at: draft.absoluteDeadlineAt }
        : {}),
    })),
});

// ─── Preview (POST /sla/organizations/{org}/preview) ─────────────────────────

/**
 * La preview non ha un campo revisione: il riferimento temporale (`reference_at`)
 * e' l'unico aggancio riproducibile del contratto (`slaModel.ts`). La coerenza
 * con la revisione del modello e' quindi responsabilita' del client, che non
 * deve applicare una stima di una revisione diversa da quella a schermo: e' il
 * senso di `revision` nel sequencer.
 */
export const slaStartPreviewRequest = (
  draft: SlaStartDraft,
  referenceAt: string,
): SlaPreviewRequest => ({
  reference_at: referenceAt,
  definition: {
    target_type: draft.targetType,
    duration_format: draft.sourceDurationFormat,
    duration_value: draft.durationValue,
    duration_unit: draft.durationUnit,
    risk_remaining_percent: draft.riskRemainingPercent,
  },
});

/**
 * Guardia delle risposte **fuori ordine** (sez. 5 F2b).
 *
 * Il client assegna a ogni richiesta di preview un numero progressivo e un
 * token di revisione; all'arrivo della risposta applica il risultato **solo se**
 * e' ancora quello corrente. Senza questa guardia una risposta lenta di una
 * revisione precedente sovrascriverebbe la stima gia' mostrata per quella nuova.
 */
export interface SlaPreviewSequencer {
  /** Nuova richiesta: restituisce il token da presentare all'arrivo. */
  begin: () => number;
  /** La risposta con questo token e' applicabile? */
  isCurrent: (token: number) => boolean;
  /** Revisione a cui il sequencer e' agganciato: cambiarla invalida le risposte. */
  setRevision: (revision: number) => void;
  /**
   * Applica `apply` solo se la risposta e' ancora corrente, altrimenti scarta la
   * risposta senza toccare lo stato (nessun effetto, nessuna eccezione).
   */
  settle: <T>(token: number, apply: (value: T) => void, value: T) => boolean;
}

export const createSlaPreviewSequencer = (revision: number): SlaPreviewSequencer => {
  let current = 0;
  let currentRevision = revision;
  const tokens = new Map<number, number>();
  return {
    begin: () => {
      current += 1;
      tokens.set(current, currentRevision);
      return current;
    },
    isCurrent: (token) =>
      token === current && tokens.get(token) === currentRevision,
    setRevision: (next: number) => {
      currentRevision = next;
    },
    settle: (token, apply, value) => {
      if (token !== current || tokens.get(token) !== currentRevision) return false;
      apply(value);
      return true;
    },
  };
};

// ─── Resa ────────────────────────────────────────────────────────────────────

/**
 * Durata leggibile nella lingua app senza dividere per giorni solari
 * (sez. 4 F1): valore + unita' localizzata, mai convertita.
 */
export const slaStartDurationLabel = (
  durationValue: number | null,
  durationUnit: SlaDurationUnit | null,
  unitLabel: (unit: SlaDurationUnit) => string,
): string =>
  durationValue === null || durationUnit === null
    ? ''
    : `${durationValue} ${unitLabel(durationUnit)}`;

/** true quando il formato letto dal modello e' assoluto: da segnalare, non da nascondere. */
export const slaStartReadsAbsoluteFormat = (draft: SlaStartDraft): boolean =>
  draft.sourceDurationFormat === SLA_FORMAT_ABSOLUTE;
